// @flow
/* eslint-disable no-underscore-dangle */

import { createSelector } from 'reselect';

import type { FullState } from '../../../types';

import { selectFilterValue } from './filters';
import { selectFilteredList, selectProvidedList } from './results';
import {
  selectSearchLoading,
  selectSearchFirstResultLoading,
  selectSearchValue,
  selectRequiredAvailables,
} from './search';
import { selectAllTLDs } from './tlds';

const selectProvided = (_: any, provided: boolean): boolean => provided;

const _selectTldFilter = (state: FullState): string =>
  selectFilterValue(state, 'tlds');
const selectTldFilter: (state: FullState) => Array<string> = createSelector(
  [_selectTldFilter],
  value => value.split(',').filter(tld => tld !== 'suggested'),
);

const _selectPhaseFilter = (state: FullState): string =>
  selectFilterValue(state, 'phases');
const selectPhaseFilter: (state: FullState) => Array<string> = createSelector(
  [_selectPhaseFilter],
  value => value.split(','),
);

const selectSalesFilter = (state: FullState): string =>
  selectFilterValue(state, 'sales');

const likeFqdn = /^[\w-]+\.[\w]+$/;
const selectPotentialFqdnCount = (state: FullState): number => {
  const searchValue = selectSearchValue(state);
  const tldFilter = selectTldFilter(state);
  const allTLDs = selectAllTLDs(state);

  if (!searchValue || searchValue.indexOf('.') < 0) {
    return 0;
  }

  let potentialFqdns = searchValue
    .split(/[ \n]/)
    .filter(part => likeFqdn.test(part));

  if (!potentialFqdns.length) {
    return 0;
  }

  // validate the TLD against selected filter if any
  if (tldFilter.length) {
    potentialFqdns = potentialFqdns.filter(
      fqdn => tldFilter.indexOf(fqdn.split('.')[1]) > -1,
    );
  }

  // validate the TLD against available TLDs if any
  if (allTLDs.length) {
    potentialFqdns = potentialFqdns.filter(
      fqdn => allTLDs.indexOf(fqdn.split('.')[1]) > -1,
    );
  }

  return potentialFqdns.length;
};

const selectSuggestions = (
  state: FullState,
  provided: boolean,
): Array<string> =>
  !provided
    ? selectFilteredList(state, 'popularity')
    : selectProvidedList(state, 'popularity');

export const selectPlaceholders: (
  state: FullState,
  provided: boolean,
) => number = createSelector(
  [
    selectProvided,
    selectSearchLoading,
    selectSearchFirstResultLoading,
    selectPotentialFqdnCount,
    selectAllTLDs,
    selectTldFilter,
    selectPhaseFilter,
    selectSalesFilter,
    selectSuggestions,
    selectRequiredAvailables,
  ],
  (
    provided,
    searchLoading,
    firstResultLoading,
    potentialFqdnCount,
    tlds,
    tldFilter,
    phasesFilter,
    salesFilter,
    suggestions,
    requiredAvailables,
  ) => {
    if (!searchLoading) {
      return 0;
    }

    if (provided && !firstResultLoading) {
      return 0;
    }

    if (provided && searchLoading) {
      return potentialFqdnCount;
    }

    // aiosuggest doesn't return non-provided suggestions when 3+ provided domains.
    if (provided && potentialFqdnCount >= 3) {
      return 0;
    }

    const minimum = provided ? potentialFqdnCount : requiredAvailables;
    return Math.max(minimum - suggestions.length, 0);
  },
);
